import { useCallback, useState, useTransition } from 'react';
import { pick, mergeWith, isEqual } from 'lodash';
import { CalendarDay, isEqualEmptyNormalised } from '../utils';

export const FILTERS_KEY_SUFFIX = '-filters';
const FILTERS_VERSION = `-${process.env['REACT_APP_GITHUB_SHA']}`;

const saveLocalStorageFilters = (
  filtersId: string,
  updatedFiltersString: string,
) => {
  localStorage.setItem(
    `${filtersId}${FILTERS_VERSION}${FILTERS_KEY_SUFFIX}`,
    updatedFiltersString,
  );
};

export function useFilters<T>(
  filtersId: string,
  options: {
    defaultFilters?: T;
    onFiltersChange?: (x?: Partial<T>) => void;
    resetOnFirstLoad?: boolean;
    advancedFilterKeys?: Array<keyof T>;
  },
) {
  const [isFiltersChangePending, startTransition] = useTransition();
  const [filters, setFilters] = useState<T>(() => {
    const serializedFilters = localStorage.getItem(
      `${filtersId}${FILTERS_VERSION}${FILTERS_KEY_SUFFIX}`,
    );
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
    const defaultState = options.defaultFilters ?? ({} as T);

    if (options.resetOnFirstLoad || !serializedFilters) {
      return defaultState;
    }

    const parsedFilters = JSON.parse(
      serializedFilters,
      (
        _,
        value: string | { class: string; date: string } | null | undefined,
      ) => {
        if (value === null || value === undefined) {
          return value;
        }
        if (typeof value === 'string' && isoDateRegex.test(value)) {
          return new Date(value);
        }
        if (
          typeof value === 'object' &&
          value.class === 'CalendarDay' &&
          value.date
        ) {
          return new CalendarDay(value.date);
        }
        return value;
      },
    ) as T;

    return mergeWith(
      defaultState,
      parsedFilters,
      (_objValue: T, srcValue: T) => {
        if (Array.isArray(srcValue)) {
          return srcValue;
        }

        return undefined;
      },
    );
  });

  const onFiltersChange = options?.onFiltersChange;

  const handleChangeFilters = useCallback(
    (changedFilters: Partial<T>) => {
      const updatedKeys = Object.keys(changedFilters);
      const previousFilters = pick(filters, updatedKeys);
      const changeFiltersAreDifferent = !isEqual(
        changedFilters,
        previousFilters,
      );

      if (!changeFiltersAreDifferent) return;

      const updatedFilters = {
        ...filters,
        ...changedFilters,
      };
      startTransition(() => {
        setFilters(updatedFilters);
      });

      onFiltersChange?.(updatedFilters);

      saveLocalStorageFilters(filtersId, JSON.stringify(updatedFilters));
    },
    [onFiltersChange, filters, filtersId],
  );

  const advancedFiltersApplied = options.advancedFilterKeys
    ? !isEqualEmptyNormalised(
        pick(filters, options.advancedFilterKeys),
        pick(options.defaultFilters, options.advancedFilterKeys),
      )
    : false;

  const resetAdvancedFilters = useCallback(() => {
    if (!options.advancedFilterKeys || !options.defaultFilters) return;

    handleChangeFilters({
      ...filters,
      ...pick(options.defaultFilters, options.advancedFilterKeys),
    });
  }, [
    filters,
    options.advancedFilterKeys,
    options.defaultFilters,
    handleChangeFilters,
  ]);

  return {
    filters,
    handleChangeFilters,
    isFiltersChangePending,
    advancedFiltersApplied,
    resetAdvancedFilters,
  };
}
